import { ButtonProps } from "@chakra-ui/react";
import {
  ImportStatus,
  getDataSourceSupportedInputs,
  DataSourceCode,
  DataSourceType,
} from "@syla/shared/types/models/DataSourceBase";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import { IconType } from "react-icons";
import { AiOutlineSync } from "react-icons/ai";
import { FiFile, FiFileText } from "react-icons/fi";
import { MdAdd } from "react-icons/md";
import { DataSourceInputType } from "../../../../helper/dataSource/DataSourceInputType";
import { ImportMethod } from "../panels/ImportMethodSelectionPanel";

export type ImportMethodOption = {
  importType: ImportMethod;
  label: string;
  description: string;
  icon: IconType;
  colorScheme: ButtonProps["colorScheme"];
};

export function getImportMethodsAvailable(
  dataSource: DataSourceWithNetworks,
  methods: ImportMethod[],
): ImportMethodOption[] {
  const options: ImportMethodOption[] = [];

  if (methods.includes(DataSourceInputType.Api)) {
    options.push({
      importType: DataSourceInputType.Api,
      label: "Sync",
      description: syncText(dataSource),
      icon: AiOutlineSync,
      colorScheme: "green",
    });
  }

  if (methods.includes(DataSourceInputType.Csv)) {
    if (isAssistedFileImport(dataSource)) {
      options.push({
        importType: DataSourceInputType.Csv,
        label: "Assisted File Import",
        description: assistedImportText(dataSource),
        icon: FiFileText,
        colorScheme: "orange",
      });
    } else {
      options.push({
        importType: DataSourceInputType.Csv,
        label: "File Import",
        description: fileImportText(dataSource),
        icon: FiFileText,
        colorScheme: "blue",
      });
    }
  }

  if (methods.includes(DataSourceInputType.Custom)) {
    options.push({
      importType: DataSourceInputType.Custom,
      label: "Manual Import",
      description: manualImportText(),
      icon: FiFile,
      colorScheme: "gray",
    });
  }

  if (methods.includes("Empty")) {
    options.push({
      importType: "Empty",
      label: "Create an empty data source",
      description: emptyDataSourceText(dataSource),
      icon: MdAdd,
      colorScheme: "gray",
    });
  }

  return options;
}

function isAssistedFileImport(dataSource: DataSourceWithNetworks): boolean {
  return getDataSourceSupportedInputs(dataSource).file === ImportStatus.Alpha;
}

function syncText(dataSource: DataSourceWithNetworks): string {
  if (dataSource.type === DataSourceType.Exchange) {
    if (dataSource.syncAuthType === "oauth") {
      return `Connect to ${dataSource.name} to sync your transactions and keep them up to date.`;
    }

    return `Connect with API keys to sync your transactions directly from ${dataSource.name} and keep them up to date.`;
  }

  if (
    dataSource.type === DataSourceType.Blockchain ||
    dataSource.type === DataSourceType.Wallet
  ) {
    return "Enter your wallet address to sync your transactions directly from the blockchain and keep them up to date.";
  }

  return "Sync your transactions and keep them up to date.";
}

function fileImportText(dataSource: DataSourceWithNetworks): string {
  if (
    dataSource.type === DataSourceType.Exchange ||
    dataSource.type === DataSourceType.Wallet
  ) {
    return `Import a transaction file downloaded from ${dataSource.name}.`;
  }

  if (dataSource.type === DataSourceType.Blockchain) {
    return "Import a transaction file downloaded from the blockchain explorer or wallet you used.";
  }

  if (dataSource.type === DataSourceType.Import) {
    if (dataSource.code === DataSourceCode.OpeningBalances) {
      return "Complete the Opening Balances template, then upload it.";
    }

    return "Import a transaction file downloaded from the software you used.";
  }

  return "Import transactions from a downloaded file.";
}

function assistedImportText(dataSource: DataSourceWithNetworks): string {
  if (dataSource.type === DataSourceType.Exchange) {
    return `Attach the transaction file you downloaded from ${dataSource.name} and we'll add customised support for you.`;
  }

  return "Attach the transaction file you downloaded from your platform and we'll add customised support for you.";
}

function manualImportText(): string {
  return "Download the custom ledger template, complete it manually, then upload it.";
}

function emptyDataSourceText(dataSource: DataSourceWithNetworks): string {
  if (dataSource.type === DataSourceType.Wallet) {
    return "Create an empty wallet and add transactions later.";
  }

  if (dataSource.type === DataSourceType.Blockchain) {
    return "Create an empty blockchain data source and add transactions later.";
  }

  return "Create an empty data source and add transactions later.";
}
